import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Box, Drawer, Popover, Skeleton, Text, VisuallyHidden } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconMessagePlus, IconMessages, IconSparkles } from "@tabler/icons-react"
import { useEffect, useRef } from "react"
import { AskComposer } from "../../../../components/AskComposer"
import { AskErrorBanner } from "../../../../components/AskErrorBanner"
import { ChatMessage } from "../../../../components/ChatMessage"
import { SessionsSidebar } from "../../../../components/SessionsSidebar"
import type { IAskScopeSelection } from "../../../../lib/types/ask"
import type { IAskScopeSeries, IViewProps } from "./interfaces"

import classes from "./styles.module.css"

/** The greeting + starter questions shown before the first question. */
function EmptyGreeting({
  product,
  starters,
  onStarter,
}: {
  product: IViewProps["product"]
  starters: IViewProps["starters"]
  onStarter: (question: string) => void
}): React.JSX.Element {
  return (
    <div className={classes.empty}>
      <span className={classes.emptyIcon} aria-hidden>
        <IconSparkles size={28} />
      </span>
      <h2 className={classes.emptyTitle}>
        {product === "voxcanon" ? <Trans>Consult the oracle</Trans> : <Trans>Ask AI about your meetings</Trans>}
      </h2>
      <p className={classes.emptySub}>
        {product === "voxcanon" ? (
          <Trans>
            Ask anything about your sessions — what happened, who's who, what's still unresolved — and get an answer
            drawn straight from the chronicle.
          </Trans>
        ) : (
          <Trans>
            Ask a question in plain language — decisions, action points, who said what — and get an answer grounded in
            your meetings, with its sources.
          </Trans>
        )}
      </p>

      <div className={classes.exampleGrid}>
        {starters.map((starter) => (
          <button
            type="button"
            key={starter.id}
            className={classes.exampleCard}
            onClick={() => onStarter(starter.label)}
          >
            <span className={classes.exampleQ}>{starter.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/** The retrieval-scope chips under the composer: All meetings / This month /
 *  one per series (the series row hides when there are none). Single-select. */
function ScopeChips({
  scope,
  onScopeChange,
  series,
}: {
  scope: IAskScopeSelection
  onScopeChange: (scope: IAskScopeSelection) => void
  series: IAskScopeSeries[]
}): React.JSX.Element {
  const { i18n } = useLingui()
  const isActive = (kind: IAskScopeSelection["kind"], seriesId?: string): boolean =>
    scope.kind === kind && (kind !== "series" || scope.seriesId === seriesId)

  return (
    <fieldset className={classes.scopeRow}>
      <VisuallyHidden component="legend">{i18n._(msg`Limit to`)}</VisuallyHidden>
      <button
        type="button"
        className={classes.scopeChip}
        data-active={isActive("all") ? "true" : undefined}
        aria-pressed={isActive("all")}
        onClick={() => onScopeChange({ kind: "all" })}
      >
        <Trans>All meetings</Trans>
      </button>
      <button
        type="button"
        className={classes.scopeChip}
        data-active={isActive("month") ? "true" : undefined}
        aria-pressed={isActive("month")}
        onClick={() => onScopeChange({ kind: "month" })}
      >
        <Trans>This month</Trans>
      </button>
      {series.map((item) => (
        <button
          type="button"
          key={item.id}
          className={classes.scopeChip}
          data-active={isActive("series", item.id) ? "true" : undefined}
          aria-pressed={isActive("series", item.id)}
          onClick={() => onScopeChange({ kind: "series", seriesId: item.id })}
        >
          {item.name}
        </button>
      ))}
    </fieldset>
  )
}

/**
 * Ask-AI page — pure presentation. A single centered focus column: a compact
 * header (title + new-chat / history controls), the message thread (empty /
 * loading / answer states), and a sticky composer with retrieval-scope chips.
 * The page itself scrolls (no inner scroll box); the sessions rail lives in a
 * desktop popover and a mobile drawer. The smart route wrapper owns the queries,
 * the ask mutation and the composer/scope state.
 */
export const AskView = ({
  product,
  sessions,
  sessionsLoading,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  messages,
  threadLoading,
  busy,
  errorKind,
  onRetry,
  onFollowUp,
  starters,
  onStarter,
  scope,
  onScopeChange,
  series,
  composerValue,
  onComposerChange,
  onSubmit,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [mobileOpen, mobileSessions] = useDisclosure(false)
  const [historyOpen, history] = useDisclosure(false)
  const threadEndRef = useRef<HTMLDivElement>(null)

  // Keep the newest turn in view as the thread grows / the placeholder appears.
  // Scrolls the page (no inner scroll box); the sentinel's scroll-margin leaves
  // room for the sticky composer so the last message clears it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length + busy are re-run triggers; the body only touches the end sentinel ref.
  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    threadEndRef.current?.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "end" })
  }, [messages.length, busy])

  const sidebar = (
    <SessionsSidebar
      sessions={sessions}
      activeSessionId={activeSessionId}
      onSelect={(id) => {
        onSelectSession(id)
        mobileSessions.close()
        history.close()
      }}
      onNewChat={() => {
        onNewChat()
        mobileSessions.close()
        history.close()
      }}
      onDelete={onDeleteSession}
      loading={sessionsLoading}
    />
  )

  const isEmpty = messages.length === 0 && !threadLoading

  const thread = ((): React.JSX.Element => {
    if (threadLoading) {
      return (
        <>
          <Skeleton height={54} width="60%" radius="lg" style={{ alignSelf: "flex-end" }} />
          <Skeleton height={112} width="90%" radius="lg" />
          <Skeleton height={54} width="55%" radius="lg" style={{ alignSelf: "flex-end" }} />
        </>
      )
    }
    if (isEmpty) {
      return <EmptyGreeting product={product} starters={starters} onStarter={onStarter} />
    }
    return (
      <>
        {messages.map((message) => (
          <ChatMessage
            key={message.key}
            role={message.role}
            content={message.content}
            citations={message.citations}
            refused={message.refused}
            phase={message.phase}
            followUps={message.followUps}
            onFollowUp={onFollowUp}
          />
        ))}
      </>
    )
  })()

  return (
    <div className={classes.page}>
      <div className={classes.column}>
        <div className={classes.header}>
          <Text className={classes.headerTitle} fw={700}>
            {product === "voxcanon" ? <Trans>Oracle</Trans> : <Trans>Ask AI</Trans>}
          </Text>
          <div className={classes.headerActions}>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={onNewChat}
              aria-label={i18n._(msg`New chat`)}
              title={i18n._(msg`New chat`)}
              data-testid="ask-header-new-chat"
            >
              <IconMessagePlus size={18} />
            </ActionIcon>

            <Box visibleFrom="md">
              <Popover
                opened={historyOpen}
                onChange={(opened) => (opened ? history.open() : history.close())}
                position="bottom-end"
                width={340}
                withArrow
                shadow="md"
                trapFocus
              >
                <Popover.Target>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={history.toggle}
                    aria-label={i18n._(msg`Recent chats`)}
                    title={i18n._(msg`Recent chats`)}
                    data-testid="ask-history-desktop"
                  >
                    <IconMessages size={18} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown className={classes.historyPop}>{sidebar}</Popover.Dropdown>
              </Popover>
            </Box>

            <Box hiddenFrom="md">
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={mobileSessions.open}
                aria-label={i18n._(msg`Recent chats`)}
                title={i18n._(msg`Recent chats`)}
                data-testid="ask-history-mobile"
              >
                <IconMessages size={18} />
              </ActionIcon>
            </Box>
          </div>
        </div>

        <div className={classes.thread} data-empty={isEmpty ? "true" : undefined}>
          {thread}
          <div ref={threadEndRef} className={classes.sentinel} />
        </div>

        {errorKind ? <AskErrorBanner kind={errorKind} onRetry={onRetry} /> : null}

        <div className={classes.composerWrap}>
          <AskComposer value={composerValue} onChange={onComposerChange} onSubmit={onSubmit} busy={busy} />
          <ScopeChips scope={scope} onScopeChange={onScopeChange} series={series} />
          <Text size="xs" c="dimmed" ta="center" className={classes.hint}>
            <Trans>Answers are grounded in your meetings and cite their sources.</Trans>
          </Text>
        </div>
      </div>

      <Drawer
        opened={mobileOpen}
        onClose={mobileSessions.close}
        title={<Trans>Your chats</Trans>}
        position="left"
        size="xs"
      >
        {sidebar}
      </Drawer>
    </div>
  )
}
